// Apple-like success checkmark — spring-scales in, then draws a circle and
// stroke-animates the check path. Used across the app whenever a mutation
// succeeds and we want a delightful confirmation before dismissing.
//
// Haptic feedback: on mobile devices that expose the Vibration API, we fire a
// short "tick–tick" pattern in sync with the check drawing (~350ms after
// mount) so iOS/Android users FEEL the confirmation, not just see it.
import { useEffect } from "react";
import { motion } from "framer-motion";

export default function SuccessCheckmark({ label, size = 72 }) {
  const stroke = 4.5;
  const r = 24; // circle radius inside 60x60 viewBox
  const circumference = 2 * Math.PI * r;

  useEffect(() => {
    // Guard: not all browsers/iOS Safari expose Vibration API. If missing or
    // blocked (permissions/policies), silently no-op.
    if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
    try {
      // Short, celebratory double-tap: 15ms on, 50ms off, 25ms on.
      navigator.vibrate([15, 50, 25]);
    } catch { /* ignore vibrate errors — iOS may throw in strict mode */ }
  }, []);

  return (
    <motion.div
      className="flex flex-col items-center gap-3"
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 18 }}
      data-testid="success-checkmark"
    >
      <svg width={size} height={size} viewBox="0 0 60 60" aria-hidden>
        {/* Soft green background disc */}
        <motion.circle
          cx="30" cy="30" r={r + 3}
          fill="hsl(152 68% 45% / 0.14)"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.05 }}
          style={{ transformOrigin: "30px 30px" }}
        />
        {/* Ring drawing */}
        <motion.circle
          cx="30" cy="30" r={r}
          fill="none"
          stroke="hsl(152 68% 45%)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1], delay: 0.1 }}
          style={{ transform: "rotate(-90deg)", transformOrigin: "30px 30px" }}
        />
        {/* Checkmark stroke */}
        <motion.path
          d="M19 31 L27 39 L42 22"
          fill="none"
          stroke="hsl(152 68% 45%)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: 0.35 }}
        />
      </svg>
      {label && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.25 }}
          className="text-[14px] font-semibold text-[hsl(var(--text-strong))] tabular-nums text-center"
        >
          {label}
        </motion.div>
      )}
    </motion.div>
  );
}
